import { getWorkflowResumeActionName } from "@gencow/core";
import type { GencowCtx, GencowProcedureDef } from "@gencow/core";
import {
  allResolvableActionTokens,
  allResolvableProcedureNames,
  executeScheduledAction,
  type ScheduledActionMaps,
} from "./scheduled-action-resolver";
import { extractWorkflowResumeArgs, getWorkflowResumeDeliveryJobId } from "./workflow-resume-args";
import { requeueRecoverableWorkflowRuns, resumeWorkflowRun } from "./workflow-runner";
import { resumeWorkflowRunWithV2 } from "./workflow-kernel-v2-dispatcher";
import type { WorkflowV2CapacityPolicy } from "./workflow-kernel-v2-capacity";

interface SchedulerLike {
  registerAction(name: string, handler: (args: any) => Promise<void>): void;
  runAfter(delayMs: number, actionName: string, args: unknown, options?: { id?: string }): string;
}

interface MutationLike {
  argsSchema?: unknown;
  handler(ctx: GencowCtx, args: unknown): Promise<unknown>;
}

interface WorkflowLike {
  name: string;
}

type ResumeWorkflowRunImpl = (options: Parameters<typeof resumeWorkflowRun>[0]) => Promise<unknown>;

export function formatWorkflowResumeArgsPreview(rawArgs: unknown): string {
  try {
    return JSON.stringify(rawArgs).slice(0, 200) || "empty";
  } catch {
    return String(rawArgs) || "empty";
  }
}

export function registerSchedulerMutationActions(params: {
  scheduler: SchedulerLike;
  mutationMap: Map<string, MutationLike>;
  internalProcedureMap?: Map<string, GencowProcedureDef<"internal", any, any, any>>;
  mutationProcedureMap?: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
  actionTokenMap?: Map<string, string>;
  buildCtx: () => GencowCtx;
  parseArgs?: (schema: unknown, args: unknown) => unknown;
  executeWithTimeout: (fn: () => Promise<unknown>, type: "cron", functionName: string) => Promise<unknown>;
}): void {
  const maps: ScheduledActionMaps = {
    internalProcedureMap: params.internalProcedureMap ?? new Map(),
    mutationProcedureMap: params.mutationProcedureMap ?? new Map(),
    mutationMap: params.mutationMap,
  };
  const registered = new Set<string>();

  for (const name of allResolvableProcedureNames(maps)) {
    if (registered.has(name)) continue;
    registered.add(name);
    params.scheduler.registerAction(name, async (args: any) => {
      await executeScheduledAction({
        action: name,
        rawArgs: args ?? {},
        maps,
        buildCtx: params.buildCtx,
        parseArgs: params.parseArgs,
        executeWithTimeout: params.executeWithTimeout,
        invoker: "scheduler",
      });
    });
  }

  for (const actionToken of allResolvableActionTokens(params.actionTokenMap ?? new Map())) {
    if (registered.has(actionToken)) continue;
    registered.add(actionToken);
    params.scheduler.registerAction(actionToken, async (args: any) => {
      await executeScheduledAction({
        action: actionToken,
        rawArgs: args ?? {},
        actionTokenMap: params.actionTokenMap,
        maps,
        buildCtx: params.buildCtx,
        parseArgs: params.parseArgs,
        executeWithTimeout: params.executeWithTimeout,
        invoker: "scheduler",
      });
    });
  }

  console.log(`[scheduler] Registered ${registered.size} scheduled action(s)`);
}

export async function registerWorkflowResumeActions(params: {
  scheduler: SchedulerLike;
  workflows: WorkflowLike[];
  db: unknown;
  buildCtx: () => GencowCtx;
  executeWithTimeout: (fn: () => Promise<unknown>, type: "cron", functionName: string) => Promise<unknown>;
  isBaas: boolean;
  workflowCapacityPolicy?: WorkflowV2CapacityPolicy;
  resumeWorkflowRunImpl?: ResumeWorkflowRunImpl;
  requeueRecoverableWorkflowRunsImpl?: typeof requeueRecoverableWorkflowRuns;
}): Promise<void> {
  const resumeWorkflowRunImpl = params.resumeWorkflowRunImpl || resumeWorkflowRunWithV2;
  const requeueRecoverableWorkflowRunsImpl =
    params.requeueRecoverableWorkflowRunsImpl || requeueRecoverableWorkflowRuns;

  if (params.workflows.length === 0) return;

  for (const workflow of params.workflows) {
    const resumeAction = getWorkflowResumeActionName(workflow.name);
    params.scheduler.registerAction(resumeAction, async (rawArgs: { workflowId?: string }) => {
      const { workflowId } = extractWorkflowResumeArgs(rawArgs);
      if (!workflowId) {
        throw new Error(
          `workflowId missing for ${resumeAction} (args=${formatWorkflowResumeArgsPreview(rawArgs)})`,
        );
      }

      await params.executeWithTimeout(
        () =>
          resumeWorkflowRunImpl({
            db: params.db as any,
            workflowId,
            buildCtx: () => params.buildCtx() as any,
            capacityPolicy: params.workflowCapacityPolicy,
            scheduleResume: (resumeWorkflowId, delayMs) =>
              params.scheduler.runAfter(
                delayMs,
                resumeAction,
                { workflowId: resumeWorkflowId },
                { id: getWorkflowResumeDeliveryJobId(resumeWorkflowId) },
              ),
          }),
        "cron",
        resumeAction,
      );
    });
  }

  console.log(`[workflow] Registered ${params.workflows.length} workflow resume action(s)`);

  const recoverable = await requeueRecoverableWorkflowRunsImpl({
    db: params.db as any,
    workflowNames: params.workflows.map((workflow) => workflow.name),
    scheduleResume: (workflowName, workflowId, delayMs) => {
      const resumeAction = getWorkflowResumeActionName(workflowName);
      return params.scheduler.runAfter(
        delayMs,
        resumeAction,
        { workflowId },
        { id: getWorkflowResumeDeliveryJobId(workflowId) },
      );
    },
  });

  if (recoverable.length > 0) {
    console.log(
      `[workflow] Requeued ${recoverable.length} recoverable workflow run(s)` +
        `${params.isBaas ? " via durable scheduler" : ""}`,
    );
  }
}
